// Direct dashboard API using fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

export interface DashboardStats {
  totalTeachers: number
  totalClasses: number
  pendingAbsences: number
  activeAssignments: number
}

// Get dashboard statistics using direct fetch
export async function getDashboardStatsDirect(accessToken: string, schoolId: string): Promise<DashboardStats> {
  return handleApiCall(async () => {
    const today = new Date().toISOString().split('T')[0]

    // Build query URLs
    const teachersUrl = schoolId 
      ? `${SUPABASE_URL}/rest/v1/teachers?school_id=eq.${schoolId}&select=id`
      : `${SUPABASE_URL}/rest/v1/teachers?select=id`
    
    const classSectionsUrl = schoolId
      ? `${SUPABASE_URL}/rest/v1/class_sections?select=id,classes!inner(school_id)&classes.school_id=eq.${schoolId}`
      : `${SUPABASE_URL}/rest/v1/class_sections?select=id`
    
    const absencesUrl = schoolId
      ? `${SUPABASE_URL}/rest/v1/absences?school_id=eq.${schoolId}&date=gte.${today}&select=id`
      : `${SUPABASE_URL}/rest/v1/absences?date=gte.${today}&select=id`
    
    const assignmentsUrl = schoolId
      ? `${SUPABASE_URL}/rest/v1/assignments?school_id=eq.${schoolId}&date=eq.${today}&select=id`
      : `${SUPABASE_URL}/rest/v1/assignments?date=eq.${today}&select=id`

    // Fetch all stats in parallel
    const [teachersRes, classSectionsRes, absencesRes, assignmentsRes] = await Promise.all([
      fetch(teachersUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
      
      fetch(classSectionsUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
      
      fetch(absencesUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
      
      fetch(assignmentsUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
    ])

    // Check for errors
    if (!teachersRes.ok) {
      const error = await teachersRes.text()
      console.error('[getDashboardStatsDirect] Teachers error:', error)
    }
    if (!classSectionsRes.ok) {
      const error = await classSectionsRes.text()
      console.error('[getDashboardStatsDirect] Class sections error:', error)
    }

    // Extract counts from Content-Range header
    const getCount = (response: Response): number => {
      const contentRange = response.headers.get('content-range')
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)$/)
        if (match) return parseInt(match[1], 10)
      }
      return 0
    }

    const stats = {
      totalTeachers: getCount(teachersRes),
      totalClasses: getCount(classSectionsRes),
      pendingAbsences: getCount(absencesRes),
      activeAssignments: getCount(assignmentsRes),
    }

    return stats
  })
}
